import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Avatar
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import { useMasterPlayer } from '../../context/MasterPlayerContext';
import { getInitials, getContrastTextColor } from '../../utils/colorUtils';
import './MasterPlayerDialog.scss';

const MasterPlayerDialog = ({ open, onClose, editingPlayer = null }) => {
  const { addMasterPlayer, editMasterPlayer } = useMasterPlayer();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [error, setError] = useState('');

  const isEditing = !!editingPlayer;

  useEffect(() => {
    if (editingPlayer) {
      setFormData({
        name: editingPlayer.name || '',
        email: editingPlayer.email || '',
        phone: editingPlayer.phone || '',
        notes: editingPlayer.notes || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        notes: ''
      });
    }
    setError('');
  }, [editingPlayer, open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setError('Player name is required');
      return;
    }

    try {
      if (isEditing) {
        await editMasterPlayer(editingPlayer.id, {
          ...formData,
          name: trimmedName
        });
      } else {
        await addMasterPlayer({
          ...formData,
          name: trimmedName
        });
      }
      
      handleClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: ''
    });
    setError('');
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) {
      setError('');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      className="master-player-dialog"
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle className="dialog-title">
          <Box className="title-content">
            {isEditing ? (
              <>
                <Avatar
                  className="player-avatar"
                  style={{
                    backgroundColor: editingPlayer?.profileColor,
                    color: getContrastTextColor(editingPlayer?.profileColor)
                  }}
                >
                  {getInitials(editingPlayer?.name)}
                </Avatar>
                <Box>
                  <Typography variant="h5">
                    Edit Master Player
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update player information
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <PersonAddIcon className="title-icon" />
                <Box>
                  <Typography variant="h5">
                    Add Master Player
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add to your reusable player list
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent className="dialog-content">
          <Box className="form-fields">
            {error && (
              <Alert severity="error" className="error-alert">
                {error}
              </Alert>
            )}
            
            <TextField
              autoFocus
              fullWidth
              label="Player Name *"
              variant="outlined"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter player name"
              className="form-field"
              required
            />
            
            <TextField
              fullWidth
              label="Email (Optional)"
              variant="outlined"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="player@example.com"
              className="form-field"
            />
            
            <TextField
              fullWidth
              label="Phone (Optional)"
              variant="outlined"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="+1 (555) 123-4567"
              className="form-field"
            />
            
            <TextField
              fullWidth
              label="Notes (Optional)"
              variant="outlined"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any notes about this player..."
              multiline
              rows={3}
              className="form-field"
            />
          </Box>
        </DialogContent>
        
        <DialogActions className="dialog-actions">
          <Button 
            onClick={handleClose}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {isEditing ? 'Update Player' : 'Add Player'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default MasterPlayerDialog;
